/**
 * Which gear the car is in, for cars that do not say.
 *
 * Engine speed divided by road speed is fixed for each gear, so gears show up
 * as clusters of that ratio. The hard part is everything else that produces a
 * ratio: the clutch slipping as the car pulls away, the clutch held down
 * through a shift, rolling in neutral, reversing, and vehicle speed arriving
 * in whole km/h, which at walking pace is a large fraction of the reading.
 * Learned from naively, those invent gears -- an earlier version filled its
 * table with clutch-slip ratios and reported an eighth gear in a six-speed car.
 *
 * So a ratio is only learned while the drivetrain is demonstrably locked:
 * engine speed tracking road speed to within what whole-km/h readings allow,
 * with no drift in the ratio, and -- when the engine is anywhere near idle with
 * the pedal up, which is what neutral looks like -- for seconds rather than a
 * fraction of one.
 *
 * Numbering is anchored on the gear the car pulls away in before shifting up,
 * and the rest are fitted against the ratio steps of a typical gearbox, which
 * is what places a gear correctly while the ones around it are still unseen
 * and what rejects a stray ratio that no gearbox would have. Nothing is ever
 * numbered beyond the number of gears the car has.
 */

import type { Transmission } from '../types.js';

export interface LearnedGear {
  /** Engine rpm per km/h: the middle of everything seen at this gear. */
  ratio: number;
  /**
   * The lowest ratio this gear has credibly shown, which is the gear itself.
   *
   * A torque converter only ever lets the engine run *ahead* of the gearbox,
   * so every sample sits at or above the true ratio and the mean sits above it
   * by however much the car happened to be slipping. The low edge does not,
   * which matters because gear numbering is worked out from the size of the
   * steps between gears -- and slip, being larger in the lower gears, distorts
   * exactly those steps. Absent on gears learned before this was recorded.
   */
  floor?: number;
  /** Seconds of locked-up driving observed at this ratio. */
  dwellS: number;
  /** Seconds credited to this ratio as the gear the car pulls away in. */
  launchS: number;
  /**
   * Seconds seen only in pull-aways that never shifted up -- reversing, or
   * creeping in traffic. A ratio seen in nothing else may well be reverse.
   */
  soloS: number;
  /** Averaging weight behind `ratio`; low-speed samples count for less. */
  weight: number;
  /**
   * Typical relative size of a 1 km/h step in the samples behind `ratio`.
   * Whole-km/h speed under-reads, so a ratio learned at low speed can sit up
   * to this fraction above the true one.
   */
  coarseness: number;
}

export interface GearSample {
  /** Epoch ms. */
  t: number;
  rpm: number;
  speedKph: number;
  /** Accelerator 0-100, when known. Tells neutral apart from a gear at idle revs. */
  pedal?: number;
}

export interface NumberedGear {
  gear: number;
  ratio: number;
  dwellS: number;
  /** True for the gear the car has been seen pulling away in. */
  launch: boolean;
}

/**
 * A live account of what the estimator is seeing and why it is or is not
 * learning, so a car it cannot cope with can be diagnosed from the driver's
 * seat rather than guessed at. Nothing here feeds the estimate; it is purely
 * a window onto the decision the last sample went through.
 */
export interface GearDiagnostics {
  /** The last paired reading, and the ratio it produced. */
  rpm: number;
  speedKph: number;
  ratio: number | undefined;
  /** Mean spacing between readings, ms. Learning needs these close together. */
  periodMs: number;
  /** Whether the drivetrain read as locked up on the last sample. */
  locked: boolean;
  /** How the lock-up test went on the last sample. */
  lock: LockProbe;
  /** Why the last sample taught it nothing, or undefined if it did. */
  rejected: GearRejection | undefined;
  /** Locked seconds banked across every candidate ratio. */
  dwellS: number;
  clusters: number;
  idleRpm: number | undefined;
}

/**
 * Why `lockedRatio` accepted or refused the last window. The two that matter
 * on a car with a torque converter are `scatter` and `drift`: the converter
 * lets engine speed wander against road speed, so the ratio never sits as
 * still as a locked clutch makes it.
 */
export interface LockProbe {
  samples: number;
  spanMs: number;
  spanRequiredMs: number;
  /** Worst-case disagreement across the window, in km/h, and its allowance. */
  scatter: number;
  scatterAllowed: number;
  /** Trend in the ratio across the window, in log units, and its allowance. */
  drift: number;
  driftAllowed: number;
  why: LockVerdict;
}

export type LockVerdict =
  | 'locked'
  | 'too-few-samples'
  | 'window-too-short'
  | 'stopped-or-stalled'
  | 'scatter'
  | 'drift';

export const LOCK_VERDICT_LABELS: Record<LockVerdict, string> = {
  locked: 'Locked up',
  'too-few-samples': 'Not enough readings - poll faster',
  'window-too-short': 'Readings too far apart - poll faster',
  'stopped-or-stalled': 'Stopped, or engine not running',
  scatter: 'Engine and road speed disagree - converter or clutch slipping',
  drift: 'Ratio still trending - mid-change, or slip varying',
};

const NO_LOCK: LockProbe = {
  samples: 0, spanMs: 0, spanRequiredMs: 0, scatter: 0, scatterAllowed: 0,
  drift: 0, driftAllowed: 0, why: 'too-few-samples',
};

export type GearRejection =
  | 'stationary'
  | 'below-learning-speed'
  | 'engine-near-idle'
  | 'ratio-not-steady';

export const GEAR_REJECTION_LABELS: Record<GearRejection, string> = {
  stationary: 'Not moving',
  'below-learning-speed': 'Too slow to learn from',
  'engine-near-idle': 'Engine near idle — could be neutral',
  'ratio-not-steady': 'Ratio still moving — clutch, converter or a change',
};

/** Reported while stationary, in neutral, or with the clutch down. */
export const GEAR_NEUTRAL = 0;
/** Reported while moving in a gear that cannot be identified (yet). */
export const GEAR_UNKNOWN = -1;

/**
 * The lock-up check looks back this far, stretched on a slow adapter so the
 * window always holds a few samples.
 */
const MIN_SHORT_WINDOW_MS = 450;
const MAX_SHORT_WINDOW_MS = 2500;
/** Near idle, a steady ratio has to hold this long before it is believed. */
const LONG_WINDOW_MS = 3000;
/** A new gear on the display needs this much agreeing history. */
const SWITCH_WINDOW_MS = 250;
const MIN_WINDOW_SAMPLES = 3;

/** Below this a 1 km/h step is more than a tenth of the reading. */
const MIN_LEARN_KPH = 10;
const MIN_DISPLAY_KPH = 4;
const MIN_RUNNING_RPM = 400;

/** Ratios closer than this are the same gear. Real adjacent gears are 8-80% apart. */
const SAME_GEAR = 0.05;
const CONFIRM_DWELL_S = 2;
/**
 * Locked time a gear needs within one pull-away to be the one it was made in.
 * Brief, because a brisk driver is through first gear in half a second; the
 * shift up that must follow is what makes a single pull-away convincing.
 */
const LAUNCH_HOLD_S = 0.15;
const MAX_CANDIDATES = 16;
/** Longest sample gap that still counts as continuous driving. */
const MAX_STEP_S = 1.5;
/** Caps how much an established ratio resists new evidence. */
const MAX_CENTROID_WEIGHT = 60;
/** Per second the low edge creeps back up when nothing contradicts it. */
const FLOOR_DECAY = 0.002;

/**
 * A pull-away is over once the car is this fast, or has been moving this long.
 * Fast enough that even a driver who revs out second has changed up by then.
 */
const LAUNCH_END_KPH = 60;
const LAUNCH_END_MS = 20_000;
/**
 * Where first gear sits relative to second. A car pulled away in second shows
 * an established ratio in this band above its launch gear.
 */
const FIRST_OVER_SECOND = [1.4, 2.2] as const;

/**
 * How much better the best unanchored numbering must be than the next one
 * before it is trusted, in the fit's own squared-sigma units.
 *
 * Calibrated by numbering every contiguous run of gears a driver could
 * actually use: on a rigid drivetrain this admits four fits in five and gets
 * none of them wrong, which is why it is only trusted there.
 */
const NUMBERING_MARGIN = 1;

/** How long a new reading must persist before the display changes to it. */
const HOLD_MS = { gear: 200, neutral: 500, unknown: 700 } as const;

// Costs for fitting gear numbers, in units of squared standard deviations.
/** Typical spread of a real ratio step around the template, in log units. */
const STEP_SIGMA = 0.1;
/** Per gear number skipped. A gap has to explain a real mismatch to be chosen. */
const GAP_COST = 1.5;
/**
 * For calling the launch gear second rather than first. High enough that a
 * close-ratio gearbox, whose first-to-second step looks like an ordinary
 * box's second-to-third, is not renumbered on that alone.
 */
const SECOND_GEAR_LAUNCH_COST = 5;

interface WindowSample {
  t: number;
  rpm: number;
  kph: number;
}

interface Launch {
  since: number;
  /** Locked seconds seen in each gear during this pull-away. */
  dwell: Map<LearnedGear, number>;
}

export class GearEstimator {
  private clusters: LearnedGear[] = [];
  private numbered: { cluster: LearnedGear; gear: number }[] = [];
  /** False until gear numbers can be trusted rather than merely ordered. */
  private numberingKnown = false;
  private numberingKey = '';
  /** How much better the chosen numbering was than the next explanation. */
  private numberingMargin = 0;
  private window: WindowSample[] = [];
  private lastT = 0;
  /** Smoothed time between samples. */
  private periodMs = 0;
  private idle: number | undefined;
  private launch: Launch | null = null;
  private shown = GEAR_NEUTRAL;
  private pending = GEAR_NEUTRAL;
  private pendingSince = 0;
  private diag: GearDiagnostics = {
    rpm: 0, speedKph: 0, ratio: undefined, periodMs: 0, locked: false,
    lock: NO_LOCK, rejected: 'stationary', dwellS: 0, clusters: 0, idleRpm: undefined,
  };
  /** Filled in by the last lock test, for the diagnostics only. */
  private probe: LockProbe = NO_LOCK;

  constructor(private gearCount = 6, private transmission: Transmission = 'manual') {
    this.gearCount = clampGearCount(gearCount);
  }

  setTransmission(t: Transmission): void {
    if (t === this.transmission) return;
    this.transmission = t;
    this.numberingKey = '';
    this.renumber();
  }

  /**
   * Whether engine speed over road speed is the gear ratio, or the gear ratio
   * plus however much the drivetrain is slipping.
   *
   * Only a rigid coupling makes the ratio mean the gear. A torque converter
   * inflates it by an amount that varies with load and is largest in the low
   * gears, which distorts the steps between gears -- and the steps are what
   * gear numbering is worked out from. Measured against a slipping converter,
   * numbering from the ratio pattern alone is wrong often enough not to show.
   */
  private get rigid(): boolean {
    return this.transmission === 'manual' || this.transmission === 'dct';
  }

  setGearCount(n: number): void {
    const next = clampGearCount(n);
    if (next === this.gearCount) return;
    this.gearCount = next;
    this.renumber();
  }

  /** Learned idle speed, from time spent stationary with the pedal up. */
  get idleRpm(): number | undefined {
    return this.idle;
  }

  /** Whether gear numbers can be trusted yet, as opposed to merely ordered. */
  get numberingIsKnown(): boolean {
    return this.numberingKnown;
  }

  /**
   * How decisively the numbering beat the next explanation, where it was
   * worked out from the ratio pattern rather than from a pull-away. Zero when
   * a pull-away anchored it, or when nothing is numbered yet.
   */
  get numberingMarginValue(): number {
    return this.numberingMargin;
  }

  /** What the last sample was judged to be, and why. Read-only. */
  get diagnostics(): Readonly<GearDiagnostics> {
    return this.diag;
  }

  /** Gears as currently numbered, lowest gear first. */
  gears(): NumberedGear[] {
    const launch = this.launchCluster();
    return this.numbered.map(({ cluster, gear }) => ({
      gear,
      ratio: cluster.ratio,
      dwellS: cluster.dwellS,
      launch: cluster === launch,
    }));
  }

  /**
   * Feed one paired engine-speed and road-speed reading, and get back the gear
   * to display: 1 and up, `GEAR_NEUTRAL`, or `GEAR_UNKNOWN`.
   */
  update(s: GearSample): number {
    // A CVT has no discrete ratios, so there is nothing to learn and nothing
    // honest to report. Saying so beats offering a number that means nothing.
    if (this.transmission === 'cvt') {
      this.probe = NO_LOCK;
      this.record(s, undefined, 'stationary');
      return s.speedKph <= 1 ? GEAR_NEUTRAL : GEAR_UNKNOWN;
    }
    const stepMs = this.lastT === 0 ? 0 : s.t - this.lastT;
    const dt = clamp(stepMs / 1000, 0, MAX_STEP_S);
    this.lastT = s.t;
    if (stepMs > 0 && stepMs < 3000) {
      this.periodMs = this.periodMs === 0 ? stepMs : this.periodMs * 0.9 + stepMs * 0.1;
    }
    const pedalUp = s.pedal === undefined || s.pedal < 3;

    if (s.speedKph <= 1) {
      if (s.rpm > MIN_RUNNING_RPM && pedalUp) {
        this.idle = this.idle === undefined ? s.rpm : this.idle + (s.rpm - this.idle) * 0.05;
      }
      this.settleLaunch();
      this.launch = { since: s.t, dwell: new Map() };
      this.window = [];
      this.probe = NO_LOCK;
    this.record(s, undefined, 'stationary');
      return this.show(GEAR_NEUTRAL, s.t);
    }
    if (this.launch && (s.speedKph >= LAUNCH_END_KPH || s.t - this.launch.since >= LAUNCH_END_MS)) {
      this.settleLaunch();
    }

    this.window.push({ t: s.t, rpm: s.rpm, kph: s.speedKph });
    while (this.window.length > 0 && s.t - (this.window[0]?.t ?? s.t) > LONG_WINDOW_MS) this.window.shift();

    const atIdle = pedalUp && this.nearIdle(s.rpm);
    // Rolling in neutral holds the engine at (or a little above) idle while the
    // car slows gently -- a ratio that barely drifts over half a second. With
    // the pedal up anywhere near idle, only a ratio steady for seconds counts.
    const suspect = pedalUp && this.idleSuspect(s.rpm);
    const shortMs = clamp(this.periodMs * 3.5, MIN_SHORT_WINDOW_MS, MAX_SHORT_WINDOW_MS);
    const probe: LockProbe = { ...NO_LOCK };
    const lock = suspect
      ? lockedRatio(this.since(s.t - LONG_WINDOW_MS), LONG_WINDOW_MS * 0.8, 0.015, 1.0, probe)
      : lockedRatio(this.since(s.t - shortMs), shortMs * 0.6, 0.04, 0.5, probe);
    this.probe = probe;

    const learning = lock !== undefined && !atIdle && lock.minKph >= MIN_LEARN_KPH;
    if (learning) this.learn(lock.ratio, lock.minKph, dt, s.t);
    this.record(
      s, lock,
      learning
        ? undefined
        : lock === undefined
          ? 'ratio-not-steady'
          : atIdle
            ? 'engine-near-idle'
            : 'below-learning-speed',
    );

    const candidate =
      s.speedKph < MIN_DISPLAY_KPH || s.rpm < MIN_RUNNING_RPM || atIdle
        ? GEAR_NEUTRAL
        : this.match(s, lock !== undefined, suspect);
    return this.show(candidate, s.t);
  }

  // --------------------------------------------------------------- learning

  private since(t: number): WindowSample[] {
    let i = this.window.length;
    while (i > 0 && (this.window[i - 1]?.t ?? 0) > t) i--;
    return this.window.slice(i);
  }

  private nearIdle(rpm: number): boolean {
    if (this.idle === undefined) return rpm < 1000;
    return Math.abs(rpm - this.idle) <= Math.max(100, this.idle * 0.1);
  }

  private idleSuspect(rpm: number): boolean {
    return rpm < (this.idle === undefined ? 1400 : this.idle + 500);
  }

  private learn(ratio: number, minKph: number, dt: number, t: number): void {
    if (dt <= 0) return;
    const coarseness = 1 / minKph;
    const precision = dt * Math.min(1, minKph / 40);

    let near: LearnedGear | undefined;
    let nearDist = Infinity;
    for (const c of this.clusters) {
      const d = Math.abs(Math.log(ratio / c.ratio));
      if (d < nearDist) {
        nearDist = d;
        near = c;
      }
    }

    if (near && nearDist <= SAME_GEAR + coarseness) {
      const w = Math.min(near.weight, MAX_CENTROID_WEIGHT);
      near.ratio = Math.exp(Math.log(near.ratio) + (Math.log(ratio / near.ratio) * precision) / (w + precision));
      near.coarseness += ((coarseness - near.coarseness) * precision) / (near.weight + precision);
      near.weight += precision;
      near.dwellS += dt;
      // The low edge falls to any lower ratio at once and creeps back up only
      // slowly, so one noisy sample cannot peg it below the real gear for ever.
      const floor = near.floor ?? near.ratio;
      near.floor = ratio < floor ? ratio : floor * (1 + FLOOR_DECAY * dt);
    } else {
      near = { ratio, floor: ratio, dwellS: dt, launchS: 0, soloS: 0, weight: precision, coarseness };
      this.clusters.push(near);
      this.prune();
    }

    const launch = this.launch;
    if (launch && t - launch.since < LAUNCH_END_MS) {
      launch.dwell.set(near, (launch.dwell.get(near) ?? 0) + dt);
    }

    this.mergeClose();
    // Pull-aways only ever work up through the gears, so once a gear below one
    // that has held a lock turns up, the gear it was made in is already known.
    // "Below" means clearly so: the cluster this sample landed in may just have
    // been merged into a neighbour with a slightly different ratio.
    const shiftedUp = this.launch && [...this.launch.dwell].some(
      ([c, d]) => d >= LAUNCH_HOLD_S && Math.log(c.ratio / ratio) > SAME_GEAR + Math.max(c.coarseness, coarseness),
    );
    if (shiftedUp) {
      this.settleLaunch();
    } else {
      this.renumber();
    }
  }

  /**
   * Close a pull-away and credit the gear it was made in.
   *
   * Only a pull-away followed by a shift up counts: reverse never has one, and
   * nor does creeping along in traffic, so both are kept out of the anchor.
   * The credit goes to the lowest gear that held a real lock rather than
   * simply the first -- the last moments of a slipping clutch can pass the
   * lock test briefly, and a brisk driver can be through first gear before a
   * lock registers at all.
   */
  private settleLaunch(): void {
    const launch = this.launch;
    this.launch = null;
    if (!launch || launch.dwell.size === 0) return;
    const seen = [...launch.dwell].filter(([c]) => this.clusters.includes(c));
    const pick = seen.filter(([, d]) => d >= LAUNCH_HOLD_S).sort(([a], [b]) => b.ratio - a.ratio)[0];
    const shiftedUp =
      pick !== undefined &&
      seen.some(([c, d]) => d > 0 && Math.log(pick[0].ratio / c.ratio) > SAME_GEAR + Math.max(c.coarseness, pick[0].coarseness));

    if (pick && shiftedUp) {
      pick[0].launchS += pick[1];
    } else {
      for (const [c, d] of seen) c.soloS += d;
    }
    this.renumber();
  }

  /** Keep the candidate list bounded by dropping the least-seen ratio. */
  private prune(): void {
    while (this.clusters.length > MAX_CANDIDATES) {
      // Never the newest: a table full of old noise must not block a real gear.
      let worst = 0;
      for (let i = 1; i < this.clusters.length - 1; i++) {
        if ((this.clusters[i]?.dwellS ?? 0) < (this.clusters[worst]?.dwellS ?? 0)) worst = i;
      }
      const [removed] = this.clusters.splice(worst, 1);
      if (removed) this.launch?.dwell.delete(removed);
    }
  }

  /**
   * Fold together clusters that turned out to be one gear -- typically a ratio
   * first seen at low speed, biased high by the km/h step, and the same gear
   * seen properly later on.
   */
  private mergeClose(): void {
    this.clusters.sort((a, b) => b.ratio - a.ratio);
    for (let i = 0; i + 1 < this.clusters.length; ) {
      const a = this.clusters[i] as LearnedGear;
      const b = this.clusters[i + 1] as LearnedGear;
      if (Math.log(a.ratio / b.ratio) > SAME_GEAR + Math.max(a.coarseness, b.coarseness)) {
        i++;
        continue;
      }
      const weight = a.weight + b.weight;
      const merged: LearnedGear = {
        ratio: Math.exp((Math.log(a.ratio) * a.weight + Math.log(b.ratio) * b.weight) / (weight || 1)),
        dwellS: a.dwellS + b.dwellS,
        launchS: a.launchS + b.launchS,
        soloS: a.soloS + b.soloS,
        weight,
        coarseness: (a.coarseness * a.weight + b.coarseness * b.weight) / (weight || 1),
      };
      this.clusters.splice(i, 2, merged);
      const dwell = this.launch?.dwell;
      if (dwell && (dwell.has(a) || dwell.has(b))) {
        dwell.set(merged, (dwell.get(a) ?? 0) + (dwell.get(b) ?? 0));
        dwell.delete(a);
        dwell.delete(b);
      }
    }
  }

  private launchCluster(): LearnedGear | undefined {
    let best: LearnedGear | undefined;
    for (const c of this.clusters) {
      if (c.launchS >= LAUNCH_HOLD_S && (!best || c.launchS > best.launchS)) best = c;
    }
    return best;
  }

  /**
   * Decide which clusters are gears, and what they are called.
   *
   * The gear the car pulls away in anchors the numbering, and anything with a
   * higher ratio is reverse or a slipping clutch -- unless it is established
   * and sits one first-to-second step above, in which case the car is being
   * pulled away in second. Without a pull-away, numbers are only trusted once
   * every gear the car has is accounted for.
   */
  private renumber(): void {
    const launch = this.launchCluster();
    const eligible = this.clusters.filter(
      (c) => c === launch || (c.dwellS >= CONFIRM_DWELL_S && !(c.launchS === 0 && c.soloS >= c.dwellS * 0.8)),
    );

    let first: LearnedGear | undefined;
    let items: LearnedGear[];
    if (launch) {
      first = eligible
        .filter((c) => {
          const step = c.ratio / launch.ratio;
          return c !== launch && step >= FIRST_OVER_SECOND[0] && step <= FIRST_OVER_SECOND[1];
        })
        .sort((a, b) => b.dwellS - a.dwellS)[0];
      const below = eligible.filter((c) => c !== first && c !== launch && c.ratio < launch.ratio);
      items = [...(first ? [first] : []), launch, ...below.sort((a, b) => b.ratio - a.ratio)];
    } else {
      items = [...eligible].sort((a, b) => b.ratio - a.ratio);
    }

    // The fit is not free, and most calls change nothing that matters to it.
    const key =
      this.gearCount + '|' + (first ? 'F' : '') + '|' +
      items
        .map((c) => clusterId(c) + ':' + Math.round(Math.log(c.ratio) * 100) + ':' + Math.round(Math.log2(1 + c.dwellS)))
        .join(',');
    if (key === this.numberingKey) return;
    this.numberingKey = key;

    const anchor = first ?? launch;
    const keep = new Set([first, launch].filter((c): c is LearnedGear => c !== undefined));

    let fitted: { cluster: LearnedGear; gear: number }[];
    let margin = 0;
    if (anchor !== undefined) {
      fitted = fitGearNumbers(items, this.gearCount, {
        anchored: true,
        canBeSecond: anchor === launch && launch !== undefined,
        keep,
      }).fit;
    } else {
      // Nothing anchors the numbering, so ask what each gear the highest ratio
      // could be would imply, and see whether one answer stands out. A gearbox
      // steps widest between its low gears, so three ratios from the top of the
      // box fit 4-5-6 far better than they fit 1-2-3. Where that gap is
      // decisive the gears are numbered without ever seeing a pull-away --
      // which matters on a motorway run, and on any torque-converter car,
      // where a pull-away never produces a clean ratio to anchor on.
      let bestCost = Infinity;
      let runnerUp = Infinity;
      let bestFit: { cluster: LearnedGear; gear: number }[] = [];
      for (let g0 = 1; g0 <= this.gearCount; g0++) {
        const r = fitGearNumbers(items, this.gearCount, {
          anchored: false, canBeSecond: false, keep, startGear: g0,
        });
        if (r.fit.length === 0) continue;
        if (r.cost < bestCost) {
          runnerUp = bestCost;
          bestCost = r.cost;
          bestFit = r.fit;
        } else if (r.cost < runnerUp) {
          runnerUp = r.cost;
        }
      }
      fitted = bestFit;
      margin = runnerUp - bestCost;
    }

    this.numbered = fitted;
    this.numberingMargin = margin;
    this.numberingKnown =
      anchor !== undefined ||
      fitted.length === this.gearCount ||
      (this.rigid && margin >= NUMBERING_MARGIN);
  }

  // --------------------------------------------------------------- display

  private match(s: GearSample, locked: boolean, suspect: boolean): number {
    if (!this.numberingKnown) return GEAR_UNKNOWN;
    const ratio = s.rpm / s.speedKph;
    let best: { cluster: LearnedGear; gear: number } | undefined;
    let bestErr = Infinity;
    for (const n of this.numbered) {
      const err = Math.abs(Math.log(ratio / n.cluster.ratio));
      if (err < bestErr) {
        bestErr = err;
        best = n;
      }
    }
    if (!best || bestErr > displayTolerance(s.speedKph)) return GEAR_UNKNOWN;
    if (best.gear === this.shown || locked) return best.gear;
    if (suspect) return GEAR_UNKNOWN;

    // A gear already learned only needs a short run of agreement to be shown,
    // which keeps the display quick after a shift. A ratio merely sweeping past
    // one -- the engine falling away with the clutch down -- will not agree
    // for that long.
    const ref = best.cluster.ratio;
    const recent = this.since(s.t - SWITCH_WINDOW_MS);
    const agrees =
      recent.length >= 2 &&
      recent.every((p) => Math.abs(Math.log(p.rpm / p.kph / ref)) <= displayTolerance(p.kph));
    return agrees ? best.gear : GEAR_UNKNOWN;
  }

  /** Debounce the display so a shift does not flicker through its neighbours. */
  /** Snapshot what the last sample was judged to be. Never affects the result. */
  private record(
    s: GearSample,
    lock: { ratio: number; minKph: number } | undefined,
    rejected: GearRejection | undefined,
  ): void {
    let dwellS = 0;
    for (const c of this.clusters) dwellS += c.dwellS;
    this.diag = {
      rpm: s.rpm,
      speedKph: s.speedKph,
      ratio: s.speedKph > 0 ? s.rpm / s.speedKph : undefined,
      periodMs: this.periodMs,
      locked: lock !== undefined,
      lock: this.probe,
      rejected,
      dwellS,
      clusters: this.clusters.length,
      idleRpm: this.idle,
    };
  }

  private show(candidate: number, t: number): number {
    if (candidate === this.shown) {
      this.pending = candidate;
      return this.shown;
    }
    if (candidate !== this.pending) {
      this.pending = candidate;
      this.pendingSince = t;
    }
    const hold =
      candidate === GEAR_UNKNOWN ? HOLD_MS.unknown : candidate === GEAR_NEUTRAL ? HOLD_MS.neutral : HOLD_MS.gear;
    if (t - this.pendingSince >= hold) this.shown = candidate;
    return this.shown;
  }

  // ------------------------------------------------------------ persistence

  /** Gears worth remembering between drives. Unconfirmed candidates are noise. */
  export(): LearnedGear[] {
    return this.clusters
      .filter((c) => c.dwellS >= CONFIRM_DWELL_S || c.launchS >= LAUNCH_HOLD_S)
      .map((c) => ({ ...c }));
  }

  import(gears: readonly LearnedGear[] | undefined): void {
    // Anything may arrive here from a file, so nothing about its shape is assumed.
    this.clusters = (Array.isArray(gears) ? gears : [])
      .filter((g): g is LearnedGear => typeof g === 'object' && g !== null)
      .map((g) => ({
        ratio: g.ratio, dwellS: g.dwellS, launchS: g.launchS, soloS: g.soloS ?? 0,
        weight: g.weight, coarseness: g.coarseness,
        // Gears learned before the low edge was recorded fall back to the mean.
        floor: Number.isFinite(g.floor as number) ? (g.floor as number) : g.ratio,
      }))
      .filter(
        (g) =>
          [g.ratio, g.dwellS, g.launchS, g.soloS, g.weight, g.coarseness, g.floor].every(Number.isFinite) &&
          g.ratio > 4 && g.ratio < 400 && g.floor > 0 && g.floor <= g.ratio * 1.5 &&
          g.dwellS >= 0 && g.launchS >= 0 && g.soloS >= 0 && g.weight >= 0 && g.coarseness >= 0,
      );
    this.launch = null;
    this.numberingKey = '';
    this.mergeClose();
    this.renumber();
  }

  reset(): void {
    this.clusters = [];
    this.numbered = [];
    this.numberingKnown = false;
    this.numberingKey = '';
    this.window = [];
    this.lastT = 0;
    this.periodMs = 0;
    this.idle = undefined;
    this.launch = null;
    this.shown = GEAR_NEUTRAL;
    this.pending = GEAR_NEUTRAL;
    this.pendingSince = 0;
  }
}

/**
 * The ratio across a run of samples, if the drivetrain looks locked.
 *
 * Locked means engine speed divided by that ratio tracks the reported road
 * speed to within a whole-km/h step plus a little sampling skew -- a slipping
 * or disengaged clutch does not -- and that the ratio is not drifting.
 */
function lockedRatio(
  w: readonly WindowSample[],
  minSpanMs: number,
  driftBase: number,
  driftPerStep: number,
  probe?: LockProbe,
): { ratio: number; minKph: number } | undefined {
  const first = w[0];
  const last = w[w.length - 1];
  const say = (why: LockVerdict): undefined => {
    if (probe) probe.why = why;
    return undefined;
  };
  if (probe) {
    probe.samples = w.length;
    probe.spanMs = first && last ? last.t - first.t : 0;
    probe.spanRequiredMs = minSpanMs;
  }
  if (!first || !last || w.length < MIN_WINDOW_SAMPLES) return say('too-few-samples');
  if (last.t - first.t < minSpanMs) return say('window-too-short');

  let sumRpm = 0;
  let sumKph = 0;
  let minKph = Infinity;
  for (const p of w) {
    if (p.rpm < MIN_RUNNING_RPM || p.kph < MIN_DISPLAY_KPH) return say('stopped-or-stalled');
    sumRpm += p.rpm;
    sumKph += p.kph;
    if (p.kph < minKph) minKph = p.kph;
  }
  const ratio = sumRpm / sumKph;
  const meanKph = sumKph / w.length;

  let lo = Infinity;
  let hi = -Infinity;
  let sumT = 0;
  let sumL = 0;
  for (const p of w) {
    const resid = p.rpm / ratio - p.kph;
    if (resid < lo) lo = resid;
    if (resid > hi) hi = resid;
    sumT += p.t;
    sumL += Math.log(p.rpm / p.kph);
  }
  const scatterAllowed = 1.6 + 0.02 * meanKph;
  if (probe) {
    probe.scatter = hi - lo;
    probe.scatterAllowed = scatterAllowed;
  }
  if (hi - lo > scatterAllowed) return say('scatter');

  // Least-squares slope of log-ratio against time, as a change across the run.
  const meanT = sumT / w.length;
  const meanL = sumL / w.length;
  let num = 0;
  let den = 0;
  for (const p of w) {
    const dtp = p.t - meanT;
    num += dtp * (Math.log(p.rpm / p.kph) - meanL);
    den += dtp * dtp;
  }
  const drift = den > 0 ? Math.abs((num / den) * (last.t - first.t)) : 0;
  const driftAllowed = driftBase + driftPerStep / meanKph;
  if (probe) {
    probe.drift = drift;
    probe.driftAllowed = driftAllowed;
  }
  if (drift > driftAllowed) return say('drift');

  if (probe) probe.why = 'locked';
  return { ratio, minKph };
}

/**
 * Log of the ratio step from gear `j` to `j + 1` in a typical `n`-speed
 * manual: about 1.8 from first to second, shrinking progressively to roughly
 * 1.2 at the top. Only the shape matters; real gearboxes wander around it.
 */
function typicalStep(j: number, n: number): number {
  const top = Math.log(1.8);
  const bottom = Math.log(1 + 1.1 / n);
  if (n <= 2) return top;
  const k = Math.pow(1 - (j - 1) / (n - 2), 1.6);
  return top * k + bottom * (1 - k);
}

/** Smallest step two real adjacent gears have, by gearbox size. */
const minimumStep = (n: number): number => Math.log(n <= 6 ? 1.12 : n <= 8 ? 1.1 : 1.05);

/**
 * Give each learned ratio a gear number, dropping any that do not belong.
 *
 * A small dynamic programme over (ratio, gear number) pairs. Adjacent kept
 * ratios pay for how far their step is from the template for the gears they
 * are given; skipped numbers and dropped ratios pay a fixed price, dropping
 * an established ratio a higher one. Two ratios closer together than any real
 * pair of adjacent gears can never both be kept as neighbours.
 */
/**
 * The ratio to number a gear by. The low edge is the gear itself; the mean is
 * the gear plus whatever slip happened to be averaged in.
 */
function fitRatio(c: LearnedGear): number {
  return c.floor ?? c.ratio;
}

function fitGearNumbers(
  items: readonly LearnedGear[],
  n: number,
  opts: {
    anchored: boolean;
    canBeSecond: boolean;
    keep: ReadonlySet<LearnedGear>;
    /**
     * Unanchored only: which gear the highest-ratio kept item is taken to be.
     * Trying each in turn is what lets a set of ratios be numbered without ever
     * seeing a pull-away.
     */
    startGear?: number;
  },
): { fit: { cluster: LearnedGear; gear: number }[]; cost: number } {
  const m = items.length;
  if (m === 0) return { fit: [], cost: Infinity };

  const dropCost = (c: LearnedGear): number => (opts.keep.has(c) ? Infinity : 1 + 0.5 * Math.sqrt(c.dwellS));
  const dropBetween = (from: number, to: number): number => {
    let sum = 0;
    for (let k = from; k < to; k++) sum += dropCost(items[k] as LearnedGear);
    return sum;
  };
  // span(a, b) is the template's total log step from gear a to gear b.
  const prefix = [0, 0];
  for (let j = 1; j < n; j++) prefix.push((prefix[j] as number) + typicalStep(j, n));
  const span = (a: number, b: number): number => (prefix[b] as number) - (prefix[a] as number);
  const minStep = minimumStep(n);

  // cost[i][g]: best total with item i kept as gear g and nothing after it decided.
  const cost: number[][] = Array.from({ length: m }, () => new Array<number>(n + 1).fill(Infinity));
  const from: [number, number][][] = Array.from({ length: m }, () => new Array<[number, number]>(n + 1).fill([-1, -1]));

  if (opts.anchored) {
    // The anchor is always item 0 and is always kept.
    (cost[0] as number[])[1] = 0;
    if (opts.canBeSecond && n >= 2) (cost[0] as number[])[2] = SECOND_GEAR_LAUNCH_COST;
  } else {
    const g0 = opts.startGear ?? 1;
    for (let i = 0; i < m; i++) (cost[i] as number[])[g0] = dropBetween(0, i);
  }

  for (let i = 1; i < m; i++) {
    const ci = items[i] as LearnedGear;
    for (let g = 2; g <= n; g++) {
      for (let j = 0; j < i; j++) {
        const cj = items[j] as LearnedGear;
        const step = Math.log(fitRatio(cj) / fitRatio(ci));
        const between = dropBetween(j + 1, i);
        if (between === Infinity) continue;
        for (let h = 1; h < g; h++) {
          const base = (cost[j] as number[])[h] as number;
          if (base === Infinity) continue;
          if (g - h === 1 && step < minStep) continue;
          const miss = (step - span(h, g)) / (STEP_SIGMA * Math.sqrt(g - h));
          const total = base + between + miss * miss + GAP_COST * (g - h - 1);
          if (total < ((cost[i] as number[])[g] as number)) {
            (cost[i] as number[])[g] = total;
            (from[i] as [number, number][])[g] = [j, h];
          }
        }
      }
    }
  }

  let best = Infinity;
  let end: [number, number] = [-1, -1];
  for (let i = 0; i < m; i++) {
    const tail = dropBetween(i + 1, m);
    for (let g = 1; g <= n; g++) {
      const total = ((cost[i] as number[])[g] as number) + tail;
      if (total < best) {
        best = total;
        end = [i, g];
      }
    }
  }
  if (best === Infinity) return { fit: [], cost: Infinity };

  const out: { cluster: LearnedGear; gear: number }[] = [];
  for (let [i, g] = end; i >= 0; [i, g] = (from[i] as [number, number][])[g] as [number, number]) {
    out.unshift({ cluster: items[i] as LearnedGear, gear: g });
  }
  return { fit: out, cost: best };
}

/** Stable identities for clusters, so a merge is never mistaken for no change. */
const clusterIds = new WeakMap<LearnedGear, number>();
let nextClusterId = 1;
function clusterId(c: LearnedGear): number {
  let id = clusterIds.get(c);
  if (id === undefined) {
    id = nextClusterId++;
    clusterIds.set(c, id);
  }
  return id;
}

/** How close a ratio must be to a learned gear to count as that gear. */
const displayTolerance = (kph: number): number => 0.06 + 0.8 / kph;

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));
const clampGearCount = (n: number): number => (Number.isFinite(n) ? clamp(Math.round(n), 1, 12) : 6);
